import type { LocalDate } from "@js-joda/core";
import { parseIsoDateToLocalDateOrNull } from "web-apps/company/utils/timeAndDate";

interface FormatTipDateParams {
  readonly dateValue: string;
  readonly isYearVisible?: boolean;
  readonly locale: string;
}

interface LocalizedDateLabels {
  readonly month: string;
  readonly weekday: string;
}

interface LocalizedDateLabelsParams {
  readonly date: LocalDate;
  readonly locale: string;
}

const englishMonths = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

const englishWeekdays = [
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
  "Sunday",
];

const swedishMonths = [
  "januari",
  "februari",
  "mars",
  "april",
  "maj",
  "juni",
  "juli",
  "augusti",
  "september",
  "oktober",
  "november",
  "december",
];

const swedishWeekdays = [
  "måndag",
  "tisdag",
  "onsdag",
  "torsdag",
  "fredag",
  "lördag",
  "söndag",
];

function isEnglishLocale(locale: string): boolean {
  return locale.startsWith("en");
}

export function toEnglishOrdinalDay(day: number): string {
  const mod100 = day % 100;
  if (mod100 >= 11 && mod100 <= 13) return `${day}th`;

  const mod10 = day % 10;
  if (mod10 === 1) return `${day}st`;
  if (mod10 === 2) return `${day}nd`;
  if (mod10 === 3) return `${day}rd`;

  return `${day}th`;
}

function capitalizeFirst(value: string): string {
  return value.length > 0 ? value[0].toUpperCase() + value.slice(1) : value;
}

function getLocalizedDateLabels({
  date,
  locale,
}: LocalizedDateLabelsParams): LocalizedDateLabels {
  if (isEnglishLocale(locale)) {
    return {
      month: englishMonths[date.monthValue() - 1],
      weekday: englishWeekdays[date.dayOfWeek().value() - 1],
    };
  }

  return {
    month: swedishMonths[date.monthValue() - 1],
    weekday: swedishWeekdays[date.dayOfWeek().value() - 1],
  };
}

export function formatTipDate({
  dateValue,
  isYearVisible,
  locale,
}: FormatTipDateParams): string {
  const date = parseIsoDateToLocalDateOrNull(dateValue);
  if (!date) return dateValue;

  const { month, weekday } = getLocalizedDateLabels({
    date,
    locale,
  });
  const day = date.dayOfMonth();
  const year = isYearVisible ? ` ${date.year()}` : "";

  if (isEnglishLocale(locale)) {
    return `${capitalizeFirst(weekday)} ${toEnglishOrdinalDay(day)} of ${month}${year}`;
  }

  return `${capitalizeFirst(weekday)} ${day} ${month}${year}`;
}
